/**
 * Zod validation for Create Request form sections **after** Opportunity / Site Walk
 * (those fields are covered by {@link validateOppDetails}).
 *
 * Factories return fresh schemas — safe to memoize in components per project rules (iOS).
 */

import { z } from 'zod';

import { zodIssuesToFormValidationErrors } from './opportunityProjectFieldsSchema';
import {
  isMultilineRichHtmlFieldKey,
  multilineRichHtmlMaxLength,
} from '../utils/richContent/multilineRichHtmlFields';
import { validateTextAreaField } from '../utils/richContent/sanitizeTextArea';

import type {
  FormValidationError,
  FormValidationResult,
} from '../utils/formValidation';

const HTML_PATTERN = /<[^>]*>/;

const DEFAULT_TEXT_MAX = 50000;

function preprocessToFormString(raw: unknown): string {
  if (raw === undefined || raw === null) {
    return '';
  }
  if (typeof raw === 'boolean') {
    return raw ? 'Yes' : 'No';
  }
  return String(raw);
}

/** Optional text fields: coerce booleans/numbers, max length, block HTML. */
export function optionalSafeFormStepText(maxLen: number = DEFAULT_TEXT_MAX) {
  return z.preprocess(
    preprocessToFormString,
    z
      .string()
      .max(maxLen)
      .refine(s => s === '' || !HTML_PATTERN.test(s), {
        message: 'HTML tags are not allowed',
      }),
  );
}

/**
 * Rich TextArea fields store sanitized HTML, so the blanket HTML rejection above
 * would fail every formatted or image-bearing value on save. Validate the markup
 * against the persist allow-list instead.
 */
export function optionalRichHtmlFormStepText(fieldKey: string) {
  return z.preprocess(
    preprocessToFormString,
    z.string().superRefine((value, ctx) => {
      const result = validateTextAreaField(value, {
        maxPlainTextLength: multilineRichHtmlMaxLength(fieldKey),
        fieldLabel: fieldKey,
        allowPendingAttachmentRefs: true,
      });
      if (!result.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message });
      }
    }),
  );
}

function fieldSchema(key: string, maxLen: number) {
  return isMultilineRichHtmlFieldKey(key)
    ? optionalRichHtmlFormStepText(key)
    : optionalSafeFormStepText(maxLen);
}

function sectionObject(
  keys: readonly string[],
  maxLen: number = DEFAULT_TEXT_MAX,
) {
  const shape: Record<string, ReturnType<typeof optionalSafeFormStepText>> = {};
  for (const k of keys) {
    shape[k] = fieldSchema(k, maxLen);
  }
  return z.object(shape).passthrough();
}

/** Site Information step — excludes `siteAdditionalSiteInformation` (validated on Opportunity). */
const SITE_INFORMATION_KEYS = [
  'siteDataCenterFlooring',
  'siteRaisedFloorHeight',
  'siteWeightSupportOfFloor',
  'siteCeilingHeight',
  'siteDroppedCeilingHeight',
  'sitePlenumHeight',
  'siteSlabToDroppedCeilingHeight',
  'siteRoomLengthFtIn',
  'siteRoomWidthSqftSqm',
  'siteAdditionalSizeInformation',
] as const;

const FACILITY_POWER_KEYS = [
  'powerMainNominalLvVoltage',
  'powerAnticipatedFinalMwSize',
  'powerTotalDataCenterItLoadKw',
  'powerTotalAvailablePowerKw',
  'powerAverageItLoadPerRackKw',
  'powerMaximumItLoadPerRackKw',
  'powerNumberAndTypeInputPerRack',
  'powerExistingSiteProtectedByLargeKwUps',
  'powerModelOfExistingUps',
  'powerLevelOfRedundancy',
  'powerRequiredRedundancyForUps',
  'powerBatteryChemistry',
  'powerBatteryRuntimeRequiredMin',
  'powerSourceLocation',
  'powerDistributionVoltageToLoad',
  'powerOverheadBuswayOrUnderFloor',
  'powerBuswayAmperage',
  'powerBuswayLengthFt',
  'powerFacilityDescription',
  'powerMainNominalAmps',
  'powerLargeUpsDescription',
  'powerFireSuppressionNeeds',
  'powerUpsOutputVoltage',
  'powerTapOfBoxRequirements',
  'powerAdditionalRackPowerInfo',
] as const;

const RACK_POWER_KEYS = [
  'rackDimensionsLength',
  'rackDimensionsHeight',
  'rackDimensionsWidth',
  'rackAdditionalRackInformation',
  'rackPlatform',
  'rackQuantity',
  'rackDescription',
  'rackUse',
  'rackSeismicRated',
  'rackRequiresLiquidToChip',
  'rackMaxLoadRating',
  'rackColor',
  'rackDescribeElevations',
  'rackPowerUom',
  'rackPduMountingType',
  'rackPduCountPerRack',
  'rackPduOutletSwitchingMonitored',
  'rackPduOutletTypesQuantity',
  'rackPduPowerType',
  'rackPduMaxKwPerRack',
  'rackPduAdditionalInfo',
  'rackPduManufacturer',
  'rackPduModel',
  'rackPduInputPlugType',
  'rackPduDcPowerShelves',
  'rackPduCapacityAmps',
  'rackPduCapacityVolts',
  'rackPduCapacityKw',
  'rackPduLoadReadingsKw',
  'rackPduLocation',
] as const;

const SMALL_UPS_KEYS = [
  'smallUpsUom',
  'smallUpsManufacturer',
  'smallUpsModel',
  'powerSmallUpsDescription',
  'smallUpsSerialNumber',
  'powerTypesOfUps',
  'smallUpsInputVoltage',
  'smallUpsInputPlug',
  'powerInputPowerPhase',
  'smallUpsCapacity',
  'smallUpsLoadPercentage',
  'powerRuntime',
  'smallUpsBatteryCabinetQty',
  'smallUpsMounting',
  'smallUpsOutlets',
  'powerSmallUpsAdditionalInfo',
] as const;

const COOLING_KEYS = [
  'coolingIsExistingSystemOnsite',
  'coolingIsChilledWaterAvailable',
  'coolingTotalAvailableCapacityKw',
  'coolingAdditionalReadinessInfo',
  'coolingRequiredRedundancyForSystem',
  'coolingTempRisePrefThroughItEquipment',
  'coolingDescribeApproach',
  'coolingDescribeRearDoorLoop',
] as const;

const LIQUID_COOLING_KEYS = [
  'lcProjectRequiresLiquidCooling',
  'lcLiquidToChipDesired',
  'lcVertivSuppliesRowManifolds',
  'lcVertivSuppliesRackManifolds',
  'lcDescription',
  'lcCduType',
  'lcCduRedundancy',
  'lcLeakDetectionApproach',
  'lcTotalLiquidCoolingThermalLoadPerSfn',
  'lcNumberOfLiquidCooledRacksToBeServed',
  'lcNumberOfAirCooledRacks',
  'lcLiquidCoolingVsPeakRackItLoad',
  'lcTotalItLoadPerDataHall',
  'lcRedundancyStrategy',
  'lcEthyleneGlycolPct',
  'lcTps',
  'lcTpr',
  'lcConnectionTypePrimary',
  'lcPrimaryFlowValve',
  'lcCoolant',
  'lcTcsCoolant',
  'lcTss',
  'lcTsr',
  'lcFlowrateRequirement',
  'lcFiltration',
  'lcMaximumPressureRating',
  'lcRackPressureDrop',
  'lcExternalStaticPressureRequirement',
  'lcConnectionTypeSecondary',
  'lcSecondaryFluidNetwork',
  'lcAdditionalInformationSfn',
  'lcDatahallCeilingHeight',
  'lcDatahallFloorType',
  'lcDatahallRaisedFloorDepth',
  'lcRackArrangementWithinTheDatahall',
  'lcCduArrangementWithinTheDatahall',
  'lcCduPrimaryPipingConnections',
  'lcCduSecondaryPipingConnections',
  'lcTcsFlowPathwayDefinition',
  'lcTcsRoutingSpecialRequirements',
  'lcTcsArrangementType',
  'lcTcsRoutingLocation',
  'lcTcsRackControlType',
  'lcTcsRackConnectionSize',
  'lcTcsRackConnectionType',
  'lcTcsLoopPressureMaximumRating',
  'lcTcsLoopPipeDiameter',
  'lcRequiredTcsCoolantFlowratePerRack',
  'lcAdditionalInformationTcs',
] as const;

/** Monitoring step (Customer IT step removed in #1012). */
const MONITORING_AND_CUSTOMER_IT_KEYS = [
  'monitoringManagementUom',
  'mgmtDescribeMonitoringControlSoftwareNeeds',
  'mgmtAdditionalRemoteMgmtInfo',
] as const;

const SUPPORTING_DOCUMENTS_KEYS = [
  'supportingDocumentsUom',
  'supportingDocumentsCaptions',
] as const;

function createFeedbackSectionSchema() {
  return z
    .object({
      fb_comments: fieldSchema('fb_comments', 10000),
      fb_rating: z.preprocess((raw: unknown) => {
        if (raw === undefined || raw === null || raw === '') {
          return undefined;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? n : raw;
      }, z.number().int().min(1).max(5).optional()),
    })
    .passthrough();
}

function createSmallUpsSectionSchema() {
  const base = sectionObject(SMALL_UPS_KEYS);
  return base.extend({
    smallUpsLoadPercentage: z.preprocess((raw: unknown) => {
      const s = preprocessToFormString(raw).trim();
      if (s === '') return '';
      const n = Number(s);
      return Number.isFinite(n) ? n : s;
    }, z.union([z.literal(''), z.number().min(0, { message: 'Load Percentage must be between 0 and 100' }).max(100, { message: 'Load Percentage must be between 0 and 100' })])),
  });
}

const SECTION_SCHEMA_FACTORIES: readonly (() => z.ZodTypeAny)[] = [
  () => sectionObject(SITE_INFORMATION_KEYS),
  () => sectionObject(FACILITY_POWER_KEYS),
  () => sectionObject(RACK_POWER_KEYS),
  createSmallUpsSectionSchema,
  () => sectionObject(COOLING_KEYS),
  () => sectionObject(LIQUID_COOLING_KEYS),
  () => sectionObject(MONITORING_AND_CUSTOMER_IT_KEYS),
  () => sectionObject(SUPPORTING_DOCUMENTS_KEYS),
  () => createFeedbackSectionSchema(),
];

/**
 * Rack / PDU form fields — same rules as historical {@link createRackInfoZodSchema}.
 */
export function createRackPowerSectionSchema() {
  return sectionObject(RACK_POWER_KEYS);
}

/** Validate all non–Opportunity-detail request-form sections for save/submit. */
export function validateRequestFormSections(
  formData: Record<string, unknown>,
): FormValidationResult {
  const errors: FormValidationError[] = [];
  for (const factory of SECTION_SCHEMA_FACTORIES) {
    const schema = factory();
    const result = schema.safeParse(formData);
    if (!result.success) {
      errors.push(...zodIssuesToFormValidationErrors(result.error));
    }
  }
  return { isValid: errors.length === 0, errors };
}
